/**
 * Building elements, without a framework.
 *
 * The old panel rendered HTML on the server through a tagged template that
 * escaped every interpolation, and the rule that made it safe was that a string
 * could never become markup by accident. That rule survives the move, by a
 * different mechanism: **nothing here ever assigns `innerHTML`.** Text goes in
 * as text, attributes go in as attributes, and a promo code containing `<` is a
 * promo code containing `<`.
 *
 * It is about seventy lines because that is genuinely all a panel of this size
 * needs. A framework would bring reconciliation, which matters when state
 * changes a hundred times a second, and these screens change when somebody
 * presses something.
 */

export type Child = Node | string | number | false | null | undefined;

export interface Attributes {
  readonly [name: string]: string | number | boolean | ((event: Event) => void) | undefined;
}

/**
 * `el('p', { class: 'figure' }, 'Sent')`.
 *
 * A value starting `on` is a listener, `class` and `for` are spelled as they
 * are in HTML rather than as they are in the DOM, and `false`/`undefined`
 * attributes are left off entirely — so `disabled: false` is an absent
 * attribute rather than `disabled="false"`, which is the opposite of what it
 * says.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes: Attributes = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [name, value] of Object.entries(attributes)) {
    if (value === undefined || value === false) continue;

    if (typeof value === 'function') {
      node.addEventListener(name.replace(/^on/, '').toLowerCase(), value as EventListener);
    } else if (value === true) {
      node.setAttribute(name, '');
    } else {
      node.setAttribute(name, String(value));
    }
  }

  append(node, children);
  return node;
}

export function append(parent: Node, children: readonly Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

/** Replaces everything inside `parent`. The one place a screen is swapped. */
export function replace(parent: Element, ...children: Child[]): void {
  parent.replaceChildren();
  append(parent, children);
}

/**
 * A number as the panel writes numbers: Russian grouping, everywhere.
 *
 * Not `toLocaleString()` with the browser's locale — the panel is operated in
 * one place, and a count that reads `10,000` on one machine and `10 000` on the
 * next is a count two people cannot compare over the phone.
 */
export function number(value: number): string {
  return value.toLocaleString('ru-RU');
}

/** A timestamp as a person reads one, in the browser's own zone. */
export function moment(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';

  const at = new Date(value);
  return Number.isNaN(at.getTime())
    ? '—'
    : at.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}
